import { useStytchB2BClient, useStytchMember, useStytchMemberSession } from '@stytch/react/b2b';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ORG_SLUG } from '@/lib/stytch';

/**
 * useAuth — wrapper sobre los hooks de Stytch B2B.
 *
 * - `member` y `session` vienen del SDK (auto-refresh transparente).
 * - `signInWithEmail(email)` envía el magic link al pool admin.
 * - `signOut()` borra la sesión local y redirige a /login.
 * - `getSessionJwt()` devuelve el JWT actual para componer el header
 *   Authorization en `authorizedFetch`.
 */
export function useAuth() {
  const stytch = useStytchB2BClient();
  const { member } = useStytchMember();
  const { session } = useStytchMemberSession();
  const navigate = useNavigate();

  const signInWithEmail = useCallback(
    async (email: string) => {
      await stytch.magicLinks.email.discovery.send({
        email_address: email,
        discovery_redirect_url: `${window.location.origin}/auth/callback`,
      });
    },
    [stytch],
  );

  const signOut = useCallback(async () => {
    try {
      await stytch.session.revoke();
    } finally {
      navigate('/login', { replace: true });
    }
  }, [stytch, navigate]);

  const getSessionJwt = useCallback(() => {
    const tokens = stytch.session.getTokens();
    return tokens?.session_jwt ?? null;
  }, [stytch]);

  return {
    member,
    session,
    isAuthenticated: !!session,
    adminOrgSlug: ADMIN_ORG_SLUG,
    signInWithEmail,
    signOut,
    getSessionJwt,
  };
}
